import { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
// @ts-ignore
import 'moment/locale/ro';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/ui/Card';
import axios from 'axios';
import { LogMedicationModal } from '../components/tracking/LogMedicationModal';
import { LogMealModal } from '../components/tracking/LogMealModal';
import { LogExerciseModal } from '../components/tracking/LogExerciseModal';
import { Calendar as CalendarIcon, Edit2, Trash2, X, Check } from 'lucide-react';

// Set Romanian locale before creating localizer
moment.locale('ro');
const localizer = momentLocalizer(moment);

// Detect mobile for responsive formats
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

// 24h time formats for calendar (no AM/PM) + Romanian day/month names
const formats = {
  weekdayFormat: (date: Date, culture?: string, localizer?: any) =>
    localizer.format(date, isMobile ? 'dd' : 'dddd', culture),
  monthHeaderFormat: 'MMMM YYYY',
  dayFormat: (date: Date, culture?: string, localizer?: any) =>
    localizer.format(date, 'dd D', culture),
  timeGutterFormat: 'HH:mm',
  eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }, culture?: string, localizer?: any) =>
    `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
  agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }, culture?: string, localizer?: any) =>
    `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
  dayHeaderFormat: isMobile ? 'ddd, D MMM YYYY' : 'dddd, D MMMM YYYY',
  dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }, culture?: string, localizer?: any) =>
    `${localizer.format(start, 'D MMM', culture)} – ${localizer.format(end, 'D MMM YYYY', culture)}`,
  agendaDateFormat: 'ddd D MMM',
  agendaTimeFormat: 'HH:mm',
  agendaHeaderFormat: ({ start, end }: { start: Date; end: Date }, culture?: string, localizer?: any) =>
    `${localizer.format(start, 'D MMM YYYY', culture)} – ${localizer.format(end, 'D MMM YYYY', culture)}`,
  selectRangeFormat: ({ start, end }: { start: Date; end: Date }, culture?: string, localizer?: any) =>
    `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
};

// Romanian translations for calendar - ALL properties
const messages = {
  allDay: 'Toată ziua',
  previous: '← Înapoi',
  next: 'Înainte →',
  today: 'Azi',
  month: 'Lună',
  week: 'Săptămână',
  day: 'Zi',
  agenda: 'Agendă',
  date: 'Data',
  time: 'Ora',
  event: 'Eveniment',
  noEventsInRange: 'Nu există evenimente în această perioadă.',
  showMore: (total: number) => `+ ${total} mai multe`,
  work_week: 'Săptămână de lucru',
  yesterday: 'Ieri',
  tomorrow: 'Mâine',
};

interface TrackingEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  type: 'medication' | 'meal' | 'exercise';
  data: any;
}

interface DayStats {
  date: string;
  medications: number;
  meals: number;
  exercises: number;
  exerciseMinutes: number;
}

interface TrackingStats {
  period: string;
  dateRange: {
    start: string;
    end: string;
  };
  summary: {
    medicationCompliance: number;
    totalMealsLogged: number;
    totalExerciseMinutes: number;
    activeDays: number;
  };
  dailyStats: DayStats[];
}

export function CalendarPage() {
  // Default to month view - allDay events display best in month view
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [allLogs, setAllLogs] = useState<{ time: Date; title: string; type: string; data: any }[]>([]);
  const [stats, setStats] = useState<TrackingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logType, setLogType] = useState<'medication' | 'meal' | 'exercise'>('meal');
  // Edit log state
  const [editingLog, setEditingLog] = useState<{ time: Date; title: string; type: string; data: any } | null>(null);
  const [editTime, setEditTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    // Force Romanian locale on mount (in case another module resets it)
    moment.locale('ro');
    loadData();
  }, []);

  const loadData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Load stats for summary cards
      const statsRes = await axios.get(`${import.meta.env.VITE_API_URL}/api/tracking/stats?period=month`, { headers });
      setStats(statsRes.data);

      // Load individual logs to show events at their REAL times
      const trackingEvents: TrackingEvent[] = [];
      
      const [medsRes, mealsRes, exercisesRes] = await Promise.all([
        axios.get(`${import.meta.env.VITE_API_URL}/api/tracking/medications?limit=200`, { headers }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${import.meta.env.VITE_API_URL}/api/tracking/meals?limit=200`, { headers }).catch(() => ({ data: { logs: [] } })),
        axios.get(`${import.meta.env.VITE_API_URL}/api/tracking/exercises?limit=200`, { headers }).catch(() => ({ data: { logs: [] } })),
      ]);

      // Collect all raw events first, then space them out
      const rawEvents: { time: Date; title: string; type: 'medication' | 'meal' | 'exercise'; id: string; data: any }[] = [];

      // Medication logs
      if (medsRes.data?.logs) {
        medsRes.data.logs.forEach((log: any, idx: number) => {
          rawEvents.push({
            time: new Date(log.takenAt),
            title: `💊 ${log.medication?.name || 'Medicament'}${log.skipped ? ' (sărit)' : ''}`,
            type: 'medication',
            id: `med-${log.id || idx}`,
            data: log
          });
        });
      }

      // Meal logs
      if (mealsRes.data?.logs) {
        mealsRes.data.logs.forEach((log: any, idx: number) => {
          const mealLabel = log.mealType === 'BREAKFAST' ? 'Mic dejun' : 
                           log.mealType === 'LUNCH' ? 'Prânz' : 
                           log.mealType === 'DINNER' ? 'Cină' : 'Snack';
          rawEvents.push({
            time: new Date(log.consumedAt),
            title: `🍽️ ${mealLabel}`,
            type: 'meal',
            id: `meal-${log.id || idx}`,
            data: log
          });
        });
      }

      // Exercise logs
      if (exercisesRes.data?.logs) {
        exercisesRes.data.logs.forEach((log: any, idx: number) => {
          rawEvents.push({
            time: new Date(log.performedAt),
            title: `💪 ${log.exerciseName || 'Exercițiu'} (${log.duration || 0} min)`,
            type: 'exercise',
            id: `ex-${log.id || idx}`,
            data: log
          });
        });
      }

      // Sort all events by time
      rawEvents.sort((a, b) => a.time.getTime() - b.time.getTime());

      // Save all logs for the detail list below calendar
      setAllLogs(rawEvents);

      // Create allDay events for the calendar (no time overlap issues)
      // Group by date and type for clean display
      const dayMap = new Map<string, { meds: number; meals: number; exercises: number }>();
      
      rawEvents.forEach((raw) => {
        const dateKey = `${raw.time.getFullYear()}-${String(raw.time.getMonth() + 1).padStart(2, '0')}-${String(raw.time.getDate()).padStart(2, '0')}`;
        if (!dayMap.has(dateKey)) {
          dayMap.set(dateKey, { meds: 0, meals: 0, exercises: 0 });
        }
        const day = dayMap.get(dateKey)!;
        if (raw.type === 'medication') day.meds++;
        else if (raw.type === 'meal') day.meals++;
        else if (raw.type === 'exercise') day.exercises++;
      });

      dayMap.forEach((counts, dateKey) => {
        const [y, m, d] = dateKey.split('-').map(Number);
        const dayDate = new Date(y, m - 1, d);
        const nextDay = new Date(y, m - 1, d + 1);

        if (counts.meds > 0) {
          trackingEvents.push({
            id: `med-day-${dateKey}`,
            title: `💊 ${counts.meds} medicament${counts.meds > 1 ? 'e' : ''}`,
            start: dayDate,
            end: nextDay,
            type: 'medication',
            data: { count: counts.meds },
          });
        }
        if (counts.meals > 0) {
          trackingEvents.push({
            id: `meal-day-${dateKey}`,
            title: `🍽️ ${counts.meals} mas${counts.meals > 1 ? 'e' : 'ă'}`,
            start: dayDate,
            end: nextDay,
            type: 'meal',
            data: { count: counts.meals },
          });
        }
        if (counts.exercises > 0) {
          trackingEvents.push({
            id: `ex-day-${dateKey}`,
            title: `💪 ${counts.exercises} exerciți${counts.exercises > 1 ? 'i' : 'u'}`,
            start: dayDate,
            end: nextDay,
            type: 'exercise',
            data: { count: counts.exercises },
          });
        }
      });

      setEvents(trackingEvents);
    } catch (error) {
      console.error('Error loading tracking data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const eventStyleGetter = (event: TrackingEvent) => {
    let backgroundColor = '#3b82f6';
    
    switch (event.type) {
      case 'medication':
        backgroundColor = '#10b981';
        break;
      case 'meal':
        backgroundColor = '#f59e0b';
        break;
      case 'exercise':
        backgroundColor = '#8b5cf6';
        break;
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const handleLogActivity = async (type: 'medication' | 'meal' | 'exercise') => {
    setLogType(type);
    setShowLogModal(true);
  };

  const handleDeleteLog = async (log: { type: string; data: any }) => {
    if (!confirm('Sigur vrei să ștergi această înregistrare?')) return;
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    const id = log.data.id;
    const endpoint = log.type === 'medication' ? 'medications' : log.type === 'meal' ? 'meals' : 'exercises';
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/tracking/${endpoint}/${id}`, { headers });
      loadData(true);
    } catch (e) {
      alert('Eroare la ștergere');
    }
  };

  const handleOpenEdit = (log: { time: Date; title: string; type: string; data: any }) => {
    setEditingLog(log);
    const t = log.time;
    setEditTime(`${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`);
    setEditNotes(log.data.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editingLog) return;
    setEditSaving(true);
    const token = localStorage.getItem('accessToken');
    const headers = { Authorization: `Bearer ${token}` };
    const id = editingLog.data.id;
    const endpoint = editingLog.type === 'medication' ? 'medications' : editingLog.type === 'meal' ? 'meals' : 'exercises';
    const timeField = editingLog.type === 'medication' ? 'takenAt' : editingLog.type === 'meal' ? 'consumedAt' : 'performedAt';

    // Build new datetime: same date, new time
    const newDate = new Date(editingLog.time);
    const [h, m] = editTime.split(':').map(Number);
    newDate.setHours(h, m, 0, 0);

    try {
      await axios.put(`${import.meta.env.VITE_API_URL}/api/tracking/${endpoint}/${id}`, {
        [timeField]: newDate.toISOString(),
        notes: editNotes,
      }, { headers });
      setEditingLog(null);
      loadData(true);
    } catch (e) {
      alert('Eroare la salvare');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
        <div className="max-w-7xl mx-auto">
          <PageHeader
            title="Calendar Activități"
            subtitle="Se încarcă..."
          />
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <PageHeader
        title="Calendar Activități"
        subtitle="Urmărește-ți medicamentele, mesele și exercițiile"
        showBack={true}
        backTo="/dashboard"
        icon={<CalendarIcon className="w-5 h-5" />}
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
        {/* Stats Summary */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Card className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Conformitate Medicamente</div>
              <div className="text-2xl sm:text-3xl font-bold text-green-600">
                {stats.summary.medicationCompliance}%
              </div>
            </Card>
            <Card className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Mese Înregistrate</div>
              <div className="text-2xl sm:text-3xl font-bold text-orange-600">
                {stats.summary.totalMealsLogged}
              </div>
            </Card>
            <Card className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Minute Exerciții</div>
              <div className="text-2xl sm:text-3xl font-bold text-purple-600">
                {stats.summary.totalExerciseMinutes}
              </div>
            </Card>
            <Card className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm text-gray-600 mb-1">Zile Active</div>
              <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                {stats.summary.activeDays}
              </div>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <Card className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Acțiuni Rapide</h3>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => handleLogActivity('medication')}
              className="flex-1 min-w-[140px] px-4 sm:px-4 py-3 sm:py-2 bg-green-600 text-white text-sm sm:text-base rounded-lg hover:bg-green-700 transition active:scale-95"
            >
              💊 Log Medicament
            </button>
            <button
              onClick={() => handleLogActivity('meal')}
              className="flex-1 min-w-[140px] px-4 sm:px-4 py-3 sm:py-2 bg-orange-600 text-white text-sm sm:text-base rounded-lg hover:bg-orange-700 transition active:scale-95"
            >
              🍽️ Log Masă
            </button>
            <button
              onClick={() => handleLogActivity('exercise')}
              className="flex-1 min-w-[140px] px-4 sm:px-4 py-3 sm:py-2 bg-purple-600 text-white text-sm sm:text-base rounded-lg hover:bg-purple-700 transition active:scale-95"
            >
              💪 Log Exercițiu
            </button>
          </div>
        </Card>

        {/* Calendar */}
        <Card className="p-3 sm:p-6">
          <style>{`
            /* Remove default border on events */
            .rbc-event {
              border: none !important;
              padding: 2px 4px !important;
              font-size: 0.75rem !important;
              line-height: 1.3 !important;
              margin-bottom: 1px !important;
            }
            /* Remove the default event focus outline */
            .rbc-event:focus {
              outline: 2px solid rgba(59, 130, 246, 0.5) !important;
            }
            /* Better time slot styling */
            .rbc-time-slot {
              min-height: 20px !important;
            }
            .rbc-timeslot-group {
              min-height: 40px !important;
            }
            /* Current time indicator */
            .rbc-current-time-indicator {
              background-color: #ef4444 !important;
              height: 2px !important;
            }
            /* Better toolbar styling */
            .rbc-toolbar {
              flex-wrap: wrap !important;
              gap: 6px !important;
              margin-bottom: 12px !important;
              justify-content: center !important;
            }
            .rbc-toolbar button {
              border-radius: 8px !important;
              padding: 6px 10px !important;
              font-size: 0.8rem !important;
            }
            .rbc-toolbar button.rbc-active {
              background-color: #3b82f6 !important;
              color: white !important;
              border-color: #3b82f6 !important;
            }
            /* Month view rows */
            .rbc-month-row {
              min-height: 70px !important;
            }
            /* Day header - prevent truncation */
            .rbc-header {
              padding: 6px 2px !important;
              font-size: 0.75rem !important;
              font-weight: 600 !important;
              text-overflow: clip !important;
              overflow: visible !important;
              white-space: nowrap !important;
            }
            /* Date cell numbers */
            .rbc-date-cell {
              padding: 2px 4px !important;
              font-size: 0.8rem !important;
            }
            /* Event content - prevent overflow */
            .rbc-event-content {
              overflow: hidden !important;
              text-overflow: ellipsis !important;
              white-space: nowrap !important;
              font-size: 0.7rem !important;
            }
            /* Navigation buttons */
            .rbc-toolbar .rbc-btn-group:first-child button {
              padding: 6px 8px !important;
              font-size: 0.75rem !important;
            }
            /* Mobile-specific overrides */
            @media (max-width: 640px) {
              .rbc-toolbar {
                gap: 4px !important;
                margin-bottom: 8px !important;
              }
              .rbc-toolbar button {
                padding: 5px 8px !important;
                font-size: 0.7rem !important;
              }
              .rbc-header {
                font-size: 0.7rem !important;
                padding: 4px 1px !important;
              }
              .rbc-event {
                padding: 1px 3px !important;
                font-size: 0.65rem !important;
              }
              .rbc-event-content {
                font-size: 0.65rem !important;
              }
              .rbc-month-row {
                min-height: 60px !important;
              }
              .rbc-date-cell {
                font-size: 0.75rem !important;
                padding: 1px 3px !important;
              }
              .rbc-show-more {
                font-size: 0.6rem !important;
              }
            }
          `}</style>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ 
              height: typeof window !== 'undefined' && window.innerWidth < 768 
                ? Math.max(400, window.innerHeight - 450)
                : 600,
              minHeight: 400
            }}
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day', 'agenda']}
            messages={messages}
            formats={formats}
            culture="ro"
            dayLayoutAlgorithm="no-overlap"
            selectable
            onSelectSlot={(slotInfo: any) => setDate(slotInfo.start)}
            onSelectEvent={(event: any) => setDate(event.start)}
          />
        </Card>

        {/* Selected Day Details - shows individual logs with real times */}
        {(() => {
          const selectedDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const dayLogs = allLogs.filter((log) => {
            const logDate = `${log.time.getFullYear()}-${String(log.time.getMonth() + 1).padStart(2, '0')}-${String(log.time.getDate()).padStart(2, '0')}`;
            return logDate === selectedDateStr;
          });

          const formatTime24 = (d: Date) => {
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
          };

          const getTypeColor = (type: string) => {
            switch (type) {
              case 'medication': return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
              case 'meal': return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' };
              case 'exercise': return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' };
              default: return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
            }
          };

          return (
            <Card className="p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4">
                📋 Detalii - {date.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </h3>
              {dayLogs.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nicio activitate înregistrată în această zi. Apasă pe o zi din calendar pentru a vedea detaliile.
                </p>
              ) : (
                <div className="space-y-2">
                  {dayLogs.map((log, idx) => {
                    const colors = getTypeColor(log.type);
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 p-3 rounded-lg border ${colors.border} ${colors.bg}`}
                      >
                        <div className={`text-sm font-mono font-bold ${colors.text} min-w-[50px]`}>
                          {formatTime24(log.time)}
                        </div>
                        <div className={`text-sm font-medium ${colors.text} flex-1`}>
                          {log.title}
                        </div>
                        {log.data?.id && (
                          <div className="flex gap-1 shrink-0">
                            <button
                              onClick={() => handleOpenEdit(log)}
                              className="p-1.5 rounded-lg bg-white/60 hover:bg-white text-blue-600 hover:text-blue-800 transition"
                              title="Editează ora"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteLog(log)}
                              className="p-1.5 rounded-lg bg-white/60 hover:bg-white text-red-600 hover:text-red-800 transition"
                              title="Șterge"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })()}

        </div>
      </div>

      {/* Edit Log Modal */}
      {editingLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b">
              <h3 className="text-lg font-bold text-gray-900">Editează înregistrarea</h3>
              <button onClick={() => setEditingLog(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Activitate</p>
                <p className="font-medium text-gray-800">{editingLog.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ora</label>
                <input
                  type="time"
                  value={editTime}
                  onChange={(e) => setEditTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notițe (opțional)</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Adaugă o notiță..."
                />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button
                onClick={() => setEditingLog(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Anulează
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={editSaving}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Check className="w-4 h-4" />
                {editSaving ? 'Se salvează...' : 'Salvează'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refreshing indicator */}
      {refreshing && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-medium">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          Se actualizează...
        </div>
      )}

      {/* Modals */}
      <LogMedicationModal
          isOpen={showLogModal && logType === 'medication'}
          onClose={() => setShowLogModal(false)}
          onSuccess={() => { setDate(new Date()); loadData(true); }}
        />
        
        <LogMealModal
          isOpen={showLogModal && logType === 'meal'}
          onClose={() => setShowLogModal(false)}
          onSuccess={() => { setDate(new Date()); loadData(true); }}
        />
        
        <LogExerciseModal
          isOpen={showLogModal && logType === 'exercise'}
          onClose={() => setShowLogModal(false)}
          onSuccess={() => { setDate(new Date()); loadData(true); }}
        />
    </div>
  );
}
